// src/admin/main.tsx
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { initSentry } from './lib/sentry';
import './styles/theme.css';
import './styles/reset.css';
import './styles/typography.css';

// Catch Supabase auth-recovery tokens that landed on the wrong path.
//
// The dashboard "Send password recovery" button defaults redirect_to to the
// Site URL (no path), so Supabase's /auth/v1/verify redirects the user to
//   https://thandizo.pages.dev/#access_token=…&refresh_token=…&type=recovery
// Our _redirects then sends / -> /admin, React Router finds no match for
// /admin and <Navigate>s to /, ProtectedRoute sees no session and
// <Navigate>s to /login?next=/. Each React Router navigation drops the URL
// hash, so the recovery tokens never reach the ResetPassword screen.
//
// This runs synchronously before React mounts, so we can rewrite the URL in
// place (no reload) and let React Router start on the right route. We also
// handle the PKCE flow (?code=<pkce>) and the expired/denied error hash
// (#error=access_denied&error_code=otp_expired) so the user sees a real
// error message instead of a silent kick to the login page.
//
// Safe here because the admin console does not use OAuth — a bare ?code= or
// a recovery hash can only come from a password-reset redirect.
(() => {
  const { pathname, search, hash } = window.location;
  if (pathname === '/reset-password') return;

  const hp = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const qp = new URLSearchParams(search);

  const isRecoveryHash = hp.get('type') === 'recovery' && hp.get('access_token');
  const isRecoveryCode = qp.get('code') !== null;
  const isRecoveryError = hp.get('error_code') !== null || hp.get('error') !== null;

  if (isRecoveryHash || isRecoveryCode || isRecoveryError) {
    window.history.replaceState({}, '', `/reset-password${search}${hash}`);
  }
})();

initSentry();

const container = document.getElementById('admin-root');
if (!container) throw new Error('admin-root missing from admin.html');

createRoot(container).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
